/**
 * Firebase Web SDK Configuration
 * Initialize Firebase for web push notifications
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { logger } from '@/lib/logger';

// -----------------------------------------------------------------------------
// Minimal, hard-coded Firebase config for WEB ONLY
// Safe to expose (public config, not server key)
// TODO: Replace placeholders with your real values from Firebase Console
// Project Settings → General → Your apps → Web app
// -----------------------------------------------------------------------------
const firebaseConfig = {
  apiKey: 'AIzaSyD1TWGDGeh4YZmEznWQfHCnzIa3LDpy52Q',
  authDomain: 'munshiji-ai.firebaseapp.com',
  projectId: 'munshiji-ai',
  storageBucket: 'munshiji-ai.firebasestorage.app',
  messagingSenderId: '1073635361304',
  appId: '1:1073635361304:web:e7b26f72ee2780114bb96d',
} as const;

// VAPID key for Web Push (from Firebase Console → Cloud Messaging → Web Push certificates)
const VAPID_KEY = 'BKgnlxIYqmj94h8tzaowLYJN3g1fy5Mfp952Drf3DDWnOQ-VRJexUGW-LzL5Ua7-5MMnaJfk9GV-btv-t64pIUk';

// Initialize Firebase (reuse existing app if already created)
const existingApps = getApps();
const app: FirebaseApp = existingApps.length > 0 ? (existingApps[0] as FirebaseApp) : initializeApp(firebaseConfig);

// Get Firebase Cloud Messaging instance
let messaging: Messaging | null = null;

/**
 * Initialize Firebase Messaging for web
 * Must be called after service worker is registered
 */
export function initializeFirebaseMessaging(): Messaging | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Check if browser supports FCM
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    logger.warn('Push notifications not supported in this browser', { component: 'firebase' });
    return null;
  }

  try {
    if (!messaging) {
      messaging = getMessaging(app);
    }
    return messaging;
  } catch (error) {
    logger.error('Error initializing messaging', { error, component: 'firebase' });
    return null;
  }
}

/**
 * Get FCM token for web push notifications
 */
export async function getFCMToken(): Promise<string | null> {
  if (!messaging) {
    messaging = initializeFirebaseMessaging();
  }

  if (!messaging) {
    return null;
  }

  try {
    // VAPID key from Firebase Console → Project Settings → Cloud Messaging → Web Push certificates
    if (!VAPID_KEY || VAPID_KEY === 'YOUR_WEB_VAPID_KEY') {
      logger.error('VAPID key not configured. Set VAPID_KEY in src/lib/firebase.ts', { component: 'firebase' });
      return null;
    }

    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    
    if (token) {
      logger.info('FCM token obtained', { component: 'firebase', data: { token } });
      return token;
    } else {
      logger.warn('No registration token available. Request permission to generate one.', { component: 'firebase' });
      return null;
    }
  } catch (error) {
    logger.error('Error getting FCM token', { error, component: 'firebase' });
    return null;
  }
}

/**
 * Set up message listener for foreground notifications
 */
export function setupForegroundMessageListener(
  onMessageCallback: (payload: any) => void
): (() => void) | null {
  if (!messaging) {
    messaging = initializeFirebaseMessaging();
  }

  if (!messaging) {
    return null;
  }

  try {
    const unsubscribe = onMessage(messaging, (payload) => {
      logger.info('Message received in foreground', { component: 'firebase', data: { payload } });
      onMessageCallback(payload);
    });

    return unsubscribe;
  } catch (error) {
    logger.error('Error setting up message listener', { error, component: 'firebase' });
    return null;
  }
}

export { app, messaging };

